import { ICartRentalProduct } from '@Utilities/Cart';
import { Purchase } from './_Purchase';
import { Flatpickr } from "@Libraries/Flatpickr";
import type flatpickr from 'flatpickr';
import { TextResource } from '@Utilities/TextResource';
import { addDays, differenceInDays, isBefore, isDate } from "date-fns";

export class Rental extends Purchase
{
	private EventDateEl: HTMLInputElement;
	private EventDatePicker: flatpickr.Instance;
	private IsTuxedoEl: HTMLInputElement;
	private HeightFeetEl: HTMLSelectElement;
	private HeightInchesEl: HTMLSelectElement;
	private RentalDaysEl: HTMLSelectElement;
	public Product: ICartRentalProduct;

	constructor(form: HTMLFormElement)
	{
		super(form);

		this.EventDateEl = this.Form.Element.querySelector("input[name='EventDate']") as HTMLInputElement;
		this.IsTuxedoEl = this.Form.Element.querySelector("input[name='IsTuxedo']") as HTMLInputElement;
		this.HeightFeetEl = this.Form.Element.querySelector("select[name='HeightFeet']") as HTMLSelectElement;
		this.HeightInchesEl = this.Form.Element.querySelector("select[name='HeightInches']") as HTMLSelectElement;
		this.RentalDaysEl = this.Form.Element.querySelector("select[name='RentalDays']") as HTMLSelectElement;
		this.Type = "rental";
	}

	public override BindForm(): void
	{
		this.Form.ShowErrorHeader = false;
		this.Form.ValidationHandler = this.RentalValidator.bind(this);
		this.Form.SubmitHandler = this.AddRentalToCart.bind(this);

		this.ConfigureDatePicker();
	}

	public override GetCartProduct(): ICartRentalProduct
	{
		const cartProduct = super.GetCartProduct();
		let eventDate: Date, heightFeet: number, heightInches: number, rentalDays: number;
		let isTuxedo = false;

		if (this.EventDateEl !== null && typeof this.EventDatePicker !== "undefined")
		{
			if (this.EventDatePicker.selectedDates.length > 0)
			{
				eventDate = this.EventDatePicker.selectedDates[0];
			}
		}

		if (this.IsTuxedoEl !== null)
		{
			isTuxedo = Boolean(this.IsTuxedoEl.value.toLowerCase());
		}

		if (isTuxedo)
		{
			if (this.HeightFeetEl !== null)
			{
				const feet = this.HeightFeetEl.value;

				if (feet !== "")
				{
					heightFeet = parseInt(this.HeightFeetEl.value);
				}
				else
				{
					heightFeet = -1;
				}
			}

			if (this.HeightInchesEl !== null)
			{
				const inches = this.HeightInchesEl.value;

				if (inches !== "")
				{
					heightInches = parseInt(this.HeightInchesEl.value);
				}
				else
				{
					heightInches = -1;
				}
			}
		}
		else
		{
			heightFeet = -1;
			heightInches = -1;
		}

		if (this.RentalDaysEl !== null)
		{
			rentalDays = parseInt(this.RentalDaysEl.value);
		}

		const cartRentalProduct: ICartRentalProduct = {
			...cartProduct,
			eventDate: eventDate,
			isTuxedo: isTuxedo,
			heightFeet: heightFeet,
			heightInches: heightInches,
			rentalDays: rentalDays
		};

		return cartRentalProduct;
	};

	public AddRentalToCart(): void
	{
		this.Cart.AddRental(this.Product);
	}

	private async ConfigureDatePicker(): Promise<void>
	{
		if (this.EventDateEl !== null)
		{
			const flatpickr = await Flatpickr.Import();
			this.EventDatePicker = flatpickr(this.EventDateEl, {
				minDate: "today",
				maxDate: addDays(new Date(), 365)
			});
		}
	}

	public RentalValidator(): boolean
	{
		this.PurchaseValidator();

		if (!isDate(this.Product.eventDate))
		{
			this.Form.Errors.push(TextResource.GetTextByCulture('Feature_Shipping_SelectDate'));

			const today = new Date();
			today.setHours(0, 0, 0, 0);

			if (isBefore(this.Product.eventDate, today))
			{
				this.Form.Errors.push(TextResource.GetTextByCulture('Feature_Shipping_FutureDate'));
			}
			else if (differenceInDays(today, this.Product.eventDate) > 365)
			{
				this.Form.Errors.push(TextResource.GetTextByCulture('Feature_Shipping_FutureDateRestriction'));
			}
		}

		if (this.Product.isTuxedo)
		{
			if (this.Product.heightFeet < 0)
			{
				this.Form.Errors.push(TextResource.GetTextByCulture('Feature_Theme_SelectHeightFt'));
			}

			if (this.Product.heightInches < 0)
			{
				this.Form.Errors.push(TextResource.GetTextByCulture('Feature_Theme_SelectHeightIn'));
			}
		}

		if (this.Form.Errors.length > 0)
		{
			return false;
		}
		else
		{
			return true;
		}
	}
}